import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { buildExcelDocument } from '@/lib/excelExport';
import EntityActivityTabs from '@/components/feature/EntityActivityTabs';
import {
  formatDate, formatMoney, RESERVATION_STATUS_META, PAYMENT_STATUS_META, RESERVATION_SOURCES,
  type HebergementReservation, type ReservationStatus, type PaymentStatus,
} from '@/lib/hebergement';

interface Props {
  reservations: HebergementReservation[];
  currency: string;
  propertyName: (id: number | null) => string;
  unitName: (id: number | null) => string;
  updateReservationStatus: (id: number, status: ReservationStatus) => Promise<void>;
  updateReservationPayment: (id: number, status: PaymentStatus) => Promise<void>;
  deleteReservation: (id: number) => Promise<void>;
  onNew: () => void;
}

const FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'Toutes' },
  { key: 'upcoming', label: 'À venir' },
  { key: 'pending', label: 'En attente' },
  { key: 'confirmed', label: 'Confirmées' },
  { key: 'checked_in', label: 'En cours' },
  { key: 'cancelled', label: 'Annulées' },
];

function nextStatus(status: ReservationStatus): ReservationStatus {
  if (status === 'pending') return 'confirmed';
  if (status === 'confirmed') return 'checked_in';
  if (status === 'checked_in') return 'checked_out';
  return status;
}

function nextStatusLabel(status: ReservationStatus): string {
  if (status === 'pending') return 'Confirmer';
  if (status === 'confirmed') return 'Arrivée';
  if (status === 'checked_in') return 'Départ';
  return '';
}

export default function ReservationsView({
  reservations, currency, propertyName, unitName,
  updateReservationStatus, updateReservationPayment, deleteReservation, onNew,
}: Props) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<HebergementReservation | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [creatingExcel, setCreatingExcel] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const merchantId = getCommerceId(user);

  // Export des réservations vers une feuille Excel modifiable (app Documents).
  const handleOpenInExcel = async () => {
    if (!user || filtered.length === 0) return;
    setCreatingExcel(true);
    try {
      const headers = ['N°', 'Client', 'Email', 'Téléphone', 'Bien', 'Unité', 'Arrivée', 'Départ', 'Nuits', 'Personnes', 'Total', 'Devise', 'Statut', 'Paiement', 'Source'];
      const rows = filtered.map((r) => [
        String(r.id),
        r.guest_name || '',
        r.guest_email || '',
        r.guest_phone || '',
        propertyName(r.property_id),
        unitName(r.unit_id),
        r.check_in ? new Date(r.check_in).toLocaleDateString('fr-FR') : '',
        r.check_out ? new Date(r.check_out).toLocaleDateString('fr-FR') : '',
        String(r.nights ?? ''),
        String(r.guests ?? ''),
        String(Number(r.total) || 0),
        currency,
        RESERVATION_STATUS_META[r.status]?.label || r.status,
        PAYMENT_STATUS_META[r.payment_status]?.label || r.payment_status,
        RESERVATION_SOURCES.find((s) => s.value === r.source)?.label || r.source || '',
      ]);
      await buildExcelDocument(user.id, `Reservations ${new Date().toISOString().slice(0, 10)}`, headers, rows, { sheetName: 'Réservations' });
      navigate('/dashboard/file-manager');
    } catch { /* best-effort */ } finally {
      setCreatingExcel(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reservations.filter((r) => {
      if (q) {
        const hay = `${r.guest_name || ''} ${r.guest_email || ''} ${r.guest_phone || ''} ${propertyName(r.property_id)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filter === 'all') return true;
      if (filter === 'upcoming') return r.status !== 'cancelled' && r.check_in >= new Date().toISOString().slice(0, 10);
      return r.status === filter;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations, filter, search]);

  const advance = async (r: HebergementReservation) => {
    const target = nextStatus(r.status);
    if (target === r.status) return;
    setBusy(r.id);
    try { await updateReservationStatus(r.id, target); } finally { setBusy(null); }
  };

  const cyclePayment = async (r: HebergementReservation) => {
    const order: PaymentStatus[] = ['unpaid', 'partial', 'paid'];
    const idx = order.indexOf(r.payment_status);
    const next = order[(idx + 1) % order.length];
    setBusy(r.id);
    try { await updateReservationPayment(r.id, next); } finally { setBusy(null); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    await deleteReservation(deleting.id);
    setDeleting(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client, email, bien…"
            className="w-full pl-9 pr-3 py-2.5 rounded-full text-sm bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-300" />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={handleOpenInExcel} disabled={creatingExcel || filtered.length === 0}
            title="Créer une feuille Excel modifiable dans l'application Documents"
            className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer">
            {creatingExcel ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-excel-2-line"></i>}
            <span className="hidden sm:inline">Ouvrir dans Excel</span>
          </button>
          <button onClick={onNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-add-line"></i>Nouvelle réservation
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit overflow-x-auto max-w-full">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              filter === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-dashed border-background-300/60 rounded-lg text-center">
          <i className="ri-calendar-check-line text-3xl text-foreground-300 mb-3"></i>
          <p className="text-sm text-foreground-500">Aucune réservation</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="hidden lg:grid grid-cols-12 gap-4 px-5 py-3 bg-background-100 border-b border-background-200/70 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            <div className="col-span-3">Client</div>
            <div className="col-span-3">Bien / unité</div>
            <div className="col-span-2">Séjour</div>
            <div className="col-span-1">Total</div>
            <div className="col-span-3 text-right">Statut & actions</div>
          </div>
          <div className="divide-y divide-background-200/70">
            {filtered.map((r) => {
              const meta = RESERVATION_STATUS_META[r.status];
              const pay = PAYMENT_STATUS_META[r.payment_status];
              const src = RESERVATION_SOURCES.find((s) => s.value === r.source);
              return (
                <div key={r.id} className="border-b border-background-200/70 last:border-b-0">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 px-5 py-4 items-center">
                  <div className="lg:col-span-3 min-w-0">
                    <div className="text-sm font-semibold text-foreground-900 truncate">{r.guest_name || 'Client'}</div>
                    <div className="text-xs text-foreground-400 truncate">{r.guest_phone || r.guest_email || src?.label || '—'}</div>
                  </div>
                  <div className="lg:col-span-3 min-w-0">
                    <div className="text-sm text-foreground-700 truncate">{propertyName(r.property_id)}</div>
                    <div className="text-xs text-foreground-400 truncate">{unitName(r.unit_id)}</div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="text-xs text-foreground-600 whitespace-nowrap">{formatDate(r.check_in)} → {formatDate(r.check_out)}</div>
                    <div className="text-xs text-foreground-400">{r.nights} nuit{r.nights > 1 ? 's' : ''} · {r.guests} pers.</div>
                  </div>
                  <div className="lg:col-span-1 text-sm font-semibold text-foreground-950">{formatMoney(Number(r.total) || 0, currency)}</div>
                  <div className="lg:col-span-3 flex items-center justify-end gap-1.5 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                    <button onClick={() => cyclePayment(r)} disabled={busy === r.id} title="Changer le paiement"
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer ${pay.className}`}>
                      {pay.label}
                    </button>
                    {nextStatusLabel(r.status) && (
                      <button onClick={() => advance(r)} disabled={busy === r.id}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                        {busy === r.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-arrow-right-line"></i>}
                        {nextStatusLabel(r.status)}
                      </button>
                    )}
                    {r.status !== 'cancelled' && r.status !== 'checked_out' && (
                      <button onClick={() => updateReservationStatus(r.id, 'cancelled')} disabled={busy === r.id} title="Annuler"
                        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
                        <i className="ri-close-circle-line text-sm"></i>
                      </button>
                    )}
                    <button onClick={() => setDeleting(r)} title="Supprimer"
                      className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                    <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} title="Timeline & notes"
                      className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer">
                      <i className={expanded === r.id ? 'ri-arrow-up-s-line text-sm' : 'ri-history-line text-sm'}></i>
                    </button>
                  </div>
                  {expanded === r.id && (
                    <div className="px-5 pb-5 pt-1">
                      <EntityActivityTabs
                        commerceId={merchantId}
                        entityType="booking"
                        entityId={r.id}
                        actorName={user?.name}
                        actorUserId={user?.id}
                      />
                    </div>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {deleting && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[70]" onClick={() => setDeleting(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6">
            <h3 className="text-sm font-semibold text-foreground-950 mb-4">Supprimer la réservation de « {deleting.guest_name || 'Client'} » ?</h3>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={confirmDelete} className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white whitespace-nowrap hover:bg-red-600 transition-colors cursor-pointer">Supprimer</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}